import { twMerge } from 'tailwind-merge';
import type { RevenueReport } from '@witmetrics/utils';
import { usePracticeCurrency } from '@witmetrics/data-store';
import { formatNumber, formatPrice } from '@witmetrics/utils/strings';
import OverviewCard from '@/components/OverviewCard';
import Table, { TableBody, TableCell, TableRow } from '@/components/Table';
import SalesTableHead from './SalesTableHead';
import {
  classes,
  compareByMonth,
  exportReport,
  groupByMonth,
  styles,
  sumReportValue,
} from './utils';

export type SalesOverviewCardProps = {
  isFetchingData?: boolean;
  currentReport: RevenueReport | null;
  previousReport: RevenueReport | null;
};

export default function SalesOverviewCard({
  isFetchingData,
  currentReport,
  previousReport,
}: SalesOverviewCardProps) {
  const currency = usePracticeCurrency();

  const getCellClasses = (diff: number | null) => {
    if (!diff) return classes.cell;
    if (diff > 0) return twMerge(classes.cell, classes.cellUp);
    if (diff < 0) return twMerge(classes.cell, classes.cellDown);
  };

  const renderTableData = (
    report: RevenueReport,
    compareReport?: RevenueReport
  ) => {
    const groupedData = groupByMonth(report);
    const compareData = compareByMonth(report, compareReport);
    return (
      <>
        <TableRow hover>
          <TableCell className={classes.stickyCell}>
            Patients (Accepted)
          </TableCell>
          {groupedData.map((data, i) => (
            <TableCell
              key={i}
              className={getCellClasses(compareData[i].treatmentAccepted)}>
              {formatNumber(data.treatmentAccepted)}
            </TableCell>
          ))}
          <TableCell className={classes.summaryCell}>
            {formatNumber(sumReportValue(groupedData, 'treatmentAccepted'))}
          </TableCell>
        </TableRow>
        <TableRow hover>
          <TableCell className={classes.stickyCell}>Revenue</TableCell>
          {groupedData.map((data, i) => (
            <TableCell
              key={i}
              className={getCellClasses(compareData[i].revenue.value)}>
              {formatPrice(data.revenue.value, currency)}
            </TableCell>
          ))}
          <TableCell className={classes.summaryCell}>
            {formatPrice(sumReportValue(groupedData, 'revenue'), currency)}
          </TableCell>
        </TableRow>
      </>
    );
  };

  const renderContent = () => {
    if (!currentReport || !previousReport) return null;
    return (
      <Table>
        <SalesTableHead year={2026} />
        <TableBody sx={styles.tableBody}>
          {renderTableData(currentReport, previousReport)}
          <SalesTableHead row year={2025} />
          {renderTableData(previousReport)}
        </TableBody>
      </Table>
    );
  };

  return (
    <OverviewCard
      isFetchingData={isFetchingData}
      title="Sales Overview"
      subtitle="Patients & sales per month"
      onExport={() => exportReport(currentReport, previousReport)}>
      {renderContent()}
    </OverviewCard>
  );
}
